import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useChainStore } from "@/stores/chain";
import { api } from "@/lib/api";
import { byId } from "@/lib/chains";
import { ExplorerLink } from "@/components/ExplorerLink";
import {
  KindFilter,
  KIND_FILTER_VALUES,
  matchesKindFilter,
  type KindFilterValue,
} from "@/components/KindFilter";
import { CollectionTile, type CollectionTileData } from "@/components/CollectionTile";

type Collection = CollectionTileData & {
  description?: string;
  bannerCid?: string;
  createdAt: number;
};

export function Landing() {
  const activeChainId = useChainStore((s) => s.activeChainId);
  const [filter, setFilter] = useState<KindFilterValue>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["collections", activeChainId],
    queryFn: () => api.get<{ items: Collection[] }>(`/api/collections?chain=${activeChainId}&limit=60`),
  });
  const chain = byId(activeChainId);
  // Memoize the unwrap so downstream useMemo deps don't churn every render.
  const all = useMemo(() => data?.items ?? [], [data]);
  const ipfsGateway = chain?.ipfsGateway ?? "https://dweb.link/ipfs/";

  const counts = useMemo(() => {
    const out: Partial<Record<KindFilterValue, number>> = {};
    for (const v of KIND_FILTER_VALUES) {
      out[v] = all.filter((c) => matchesKindFilter(c, v)).length;
    }
    return out;
  }, [all]);

  const items = useMemo(() => all.filter((c) => matchesKindFilter(c, filter)), [all, filter]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <section className="mb-14">
        <p className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Atelier · open studio for NFTs
        </p>
        <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-6xl">
          Ship a collection<br />
          <span className="text-muted-foreground">before the coffee gets cold.</span>
        </h1>
        <p className="mb-7 max-w-2xl text-lg text-muted-foreground">
          Layer up some PFPs, pin the art to IPFS, deploy your own ERC-721,
          share the link. The whole loop in one tab — no clones, no curation, no
          taking a cut.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/create" className="btn-primary">Start a collection</Link>
          <Link to="/mint" className="btn-secondary">Mint a 1/1</Link>
          <Link to="/pfp" className="btn-ghost">Open the PFP studio</Link>
        </div>
        {chain && (
          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="rounded-full border border-border px-2 py-0.5">
              chain · {chain.name}
            </span>
            {chain.contracts.erc721Factory && chain.explorerUrl && (
              <span className="inline-flex items-center gap-1">
                factory{" "}
                <ExplorerLink chain={chain} kind="address" value={chain.contracts.erc721Factory} className="text-muted-foreground hover:text-foreground" />
              </span>
            )}
            {chain.contracts.atelier1of1 && chain.explorerUrl && (
              <span className="inline-flex items-center gap-1">
                1/1 contract{" "}
                <ExplorerLink chain={chain} kind="address" value={chain.contracts.atelier1of1} className="text-muted-foreground hover:text-foreground" />
              </span>
            )}
          </div>
        )}
      </section>

      <section className="mb-14">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold">Latest on {chain?.name ?? "—"}</h2>
          <span className="text-xs text-muted-foreground">
            {items.length} of {all.length} shown
          </span>
        </div>
        <div className="mb-4">
          <KindFilter value={filter} onChange={setFilter} counts={counts} />
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="card text-center">
            <p className="mb-3 text-sm text-muted-foreground">
              {all.length === 0
                ? `No collections yet on ${chain?.name ?? "this chain"}.`
                : "No collections match this filter."}
            </p>
            {all.length === 0 && (
              <Link to="/create" className="btn-primary">Be the first</Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {items.map((c, i) => (
              <CollectionTile
                key={c.id}
                c={c}
                shortName={chain?.shortName ?? `c${activeChainId}`}
                ipfsGateway={ipfsGateway}
                index={i}
              />
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {[
          {
            title: "Your contract, your rules",
            body: "Each collection lives in its own ERC-721 you own — pause, withdraw, rotate the base URI, transfer ownership. We never custody anything.",
          },
          {
            title: "PFPs in the browser",
            body: "Stack layers, weight rarities, generate the batch and download a ZIP with images + metadata. No Python scripts, no server roundtrips.",
          },
          {
            title: "Hosted mint pages",
            body: "Atelier serves /<chain>/<address> for every collection. Drop the link in Discord and your collectors are mint-ready.",
          },
        ].map((c) => (
          <div key={c.title} className="card">
            <h3 className="mb-2 font-semibold">{c.title}</h3>
            <p className="text-sm text-muted-foreground">{c.body}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
